import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Heart, Home, CheckCircle, Info } from 'lucide-react';
import { VARIANTS, KITCHEN_PACKAGES } from './constants';

export const SmartEstimatorForm = ({ 
  budget, 
  onBudgetChange, 
  carpetArea, 
  onCarpetAreaChange,
  budgetConfig, 
  onConfigChange 
}) => {
  return (
    <div className="space-y-6">
      {/* Budget & Carpet Area Input */}
      <div className="border-t pt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Budget Input */}
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6 rounded-xl border border-primary/20">
            <Label className="flex items-center gap-2 text-lg font-semibold mb-3">
              <Heart className="w-5 h-5 text-primary" /> Your Budget
            </Label>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-primary">₹</span>
              <Input
                type="number"
                className="rounded-lg text-xl font-semibold h-12"
                value={budget}
                onChange={(e) => onBudgetChange(e.target.value)}
                placeholder="e.g., 1000000"
                min="300000"
                step="50000"
                data-testid="budget-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">Minimum ₹3 Lakhs</p>
            {budget && parseFloat(budget) >= 300000 && (
              <p className="text-sm font-medium text-primary mt-1">
                ₹{(parseFloat(budget) / 100000).toFixed(2)} Lakhs
              </p>
            )}
          </div>

          {/* Carpet Area Input */}
          <div className="bg-gradient-to-r from-amber-50 to-amber-100/50 p-6 rounded-xl border border-amber-200">
            <Label className="flex items-center gap-2 text-lg font-semibold mb-3">
              <Home className="w-5 h-5 text-amber-600" /> Total Carpet Area
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                className="rounded-lg text-xl font-semibold h-12"
                value={carpetArea}
                onChange={(e) => onCarpetAreaChange(e.target.value)}
                placeholder="e.g., 1824"
                min="500"
                step="1"
                data-testid="carpet-area-input"
              />
              <span className="text-lg font-medium text-amber-700">sqft</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              We&apos;ll derive room dimensions from your carpet area
            </p>
            {carpetArea && parseFloat(carpetArea) >= 500 && (
              <p className="text-sm font-medium text-amber-700 mt-1">
                {parseFloat(carpetArea).toLocaleString('en-IN')} sqft
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Configuration */}
      <div className="border-t pt-6">
        <h3 className="text-base font-bold mb-4">Tell Us About Your Home</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <Label>Bedrooms</Label>
            <Select 
              value={String(budgetConfig.bedrooms)} 
              onValueChange={(v) => onConfigChange({...budgetConfig, bedrooms: parseInt(v)})}
            >
              <SelectTrigger className="rounded-lg mt-1 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 Bedroom</SelectItem>
                <SelectItem value="2">2 Bedrooms</SelectItem>
                <SelectItem value="3">3 Bedrooms</SelectItem>
                <SelectItem value="4">4 Bedrooms</SelectItem>
                <SelectItem value="5">5+ Bedrooms</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Kitchen Layout</Label>
            <Select 
              value={budgetConfig.kitchen_layout} 
              onValueChange={(v) => onConfigChange({...budgetConfig, kitchen_layout: v})}
            >
              <SelectTrigger className="rounded-lg mt-1 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="straight">Straight</SelectItem>
                <SelectItem value="l_shape">L-Shape</SelectItem>
                <SelectItem value="u_shape">U-Shape</SelectItem>
                <SelectItem value="parallel">Parallel</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer p-3 bg-white rounded-lg border w-full h-10">
              <input
                type="checkbox"
                checked={budgetConfig.include_living_room}
                onChange={(e) => onConfigChange({...budgetConfig, include_living_room: e.target.checked})}
                className="w-4 h-4 text-primary rounded"
              />
              <span className="text-sm">Living Room</span>
            </label>
          </div>
        </div>
      </div>

      {/* Style Selection - Cards like Room-by-Room */}
      <div className="border-t pt-6">
        <h3 className="text-base font-bold mb-4">Select Your Style</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {VARIANTS.map(v => (
            <button
              key={v.value}
              type="button"
              onClick={() => onConfigChange({...budgetConfig, preferred_variant: v.value})}
              data-testid={`style-${v.value}`}
              className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                budgetConfig.preferred_variant === v.value
                  ? 'border-primary bg-primary/5 shadow-md'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              {budgetConfig.preferred_variant === v.value && (
                <CheckCircle className="absolute top-3 right-3 w-5 h-5 text-primary" />
              )}
              <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded mb-2 ${
                v.value === 'minimalist' ? 'bg-gray-100 text-gray-700' :
                v.value === 'modern' ? 'bg-primary/10 text-primary' :
                v.value === 'traditional' ? 'bg-amber-100 text-amber-700' :
                'bg-purple-100 text-purple-700'
              }`}>
                {v.label}
              </span>
              <p className="text-sm text-gray-600">{v.description}</p>
            </button>
          ))}
        </div>
        
        {/* Style Info Banner */}
        {budgetConfig.preferred_variant && (
          <div className="mt-4 p-4 bg-primary/5 rounded-xl border border-primary/20">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-semibold text-primary">
                  {VARIANTS.find(v => v.value === budgetConfig.preferred_variant)?.label} Style
                </p>
                <p className="text-sm text-gray-600">
                  {VARIANTS.find(v => v.value === budgetConfig.preferred_variant)?.description}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {budgetConfig.preferred_variant === 'minimalist' && 'Prelam boards with matte laminate finish'}
                  {budgetConfig.preferred_variant === 'modern' && 'HDHMR with high-gloss acrylic finish'}
                  {budgetConfig.preferred_variant === 'traditional' && 'BWP plywood with natural veneer finish'}
                  {budgetConfig.preferred_variant === 'luxury' && 'Birch plywood with lacquered glass finish'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Kitchen Package Selection */}
      <div className="border-t pt-6">
        <h3 className="text-base font-bold mb-4">Select Kitchen Package</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {KITCHEN_PACKAGES.map(pkg => (
            <button
              key={pkg.value}
              type="button"
              onClick={() => onConfigChange({...budgetConfig, kitchen_package: pkg.value})}
              className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                budgetConfig.kitchen_package === pkg.value
                  ? 'border-primary bg-primary/5 shadow-md'
                  : `${pkg.color} hover:border-gray-300`
              }`}
            >
              {budgetConfig.kitchen_package === pkg.value && (
                <CheckCircle className="absolute top-3 right-3 w-5 h-5 text-primary" />
              )}
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold">{pkg.name}</span>
                <span className="text-sm text-gray-500">{pkg.priceIndicator}</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{pkg.description}</p>
              <ul className="text-xs text-gray-500 space-y-1">
                {pkg.features.slice(0, 4).map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-1">
                    <span className="text-primary mt-0.5">•</span>
                    <span>{feature}</span>
                  </li>
                ))}
                {pkg.features.length > 4 && (
                  <li className="text-primary font-medium">+{pkg.features.length - 4} more features</li>
                )}
              </ul>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SmartEstimatorForm;
